/**
 * Tank gauging — client types, the "is this a tank" predicate, and the API client.
 *
 * The feature estimates how full a floating-roof storage tank is from the crescent shadow
 * its own shell wall casts onto the roof. Design and the measured validation:
 * docs/superpowers/specs/2026-08-21-tank-gauging-design.md
 *
 * Two things worth knowing before using any of this:
 *
 *  - The backend self-disables. `getStatus()` reports `enabled: false` wherever TANK_GAUGE
 *    is unset (everywhere except the demo box), and callers hide their entry point rather
 *    than letting a button 404. Same arrangement the map assistant uses.
 *
 *  - A refusal is a result, not an error. A tank whose roof is too dark to read comes back
 *    with `ok: false` and a reason meant to be shown to the operator verbatim. Do not
 *    collapse those into a generic failure message — the reason is the useful part.
 */

const API = process.env.REACT_APP_API_URL || '';

/** Below this the crescent is a few pixels wide and the number would be noise. */
export const MIN_TANK_RADIUS_M = 18;

/** Mirrors `tank_gauge.BARRELS_PER_M3`, for totals derived client-side from stored rows. */
export const BARRELS_PER_M3 = 6.2898;

export type RoofType = 'external_floating' | 'fixed' | 'unknown';

export interface TankOverlay {
  centre: [number, number];          // [lng, lat]
  radius_m: number;
  azimuth_deg: number;
  rim_shift_px: number;
  /** [[lng, lat], [lng, lat]] — rim to the far edge of the ground shadow. */
  shadow?: [number, number][];
  /** [[lng, lat], [lng, lat]] — sun-facing rim inward across the crescent. */
  crescent?: [number, number][];
}

export interface TankReading {
  polygon_id: number;
  name?: string;
  ok: boolean;
  /** Present only when ok. */
  fill_fraction?: number;
  fill_percent?: number;
  volume_m3?: number;
  volume_bbl?: number;
  depth_m?: number;
  height_m?: number;
  radius_m?: number;
  crescent_px?: number;
  shadow_px?: number;
  sun_azimuth_deg?: number;
  sun_elevation_deg?: number | null;
  /** 'ratio' needs no sun angle; 'class_height' borrowed a height from sibling tanks. */
  method?: 'ratio' | 'class_height';
  cross_check_fill?: number | null;
  /** 1-sigma on the fill fraction, propagated from the pixel measurements. */
  fill_sigma?: number | null;
  /** The crescent was under what the imagery can resolve — read the % as a lower bound. */
  at_resolution_limit?: boolean;
  /** Class error bar in force when this reading was taken. Stored, so it survives a reload. */
  class_spread?: number | null;
  measured_at?: string | null;
  warnings?: string[];
  /** Present only when !ok. Written for the operator — show it as-is. */
  reason?: string;
  overlay?: TankOverlay;
  derived_height_m?: number | null;
}

export interface SceneInfo {
  source: string | null;             // 'WV03'
  product: string | null;            // 'Vivid'
  scene_date: string | null;         // '2025-03-14'
  native_res_m: number | null;
  sample_res_m: number | null;
  accuracy_m: number | null;
  attribution: string;
}

export interface Consistency {
  n: number;
  mean_m?: number | null;
  median_m?: number | null;
  sd_m?: number | null;
  /** Standard deviation over mean across identically built tanks. THE error bar. */
  spread: number | null;
  usable: boolean;
  /** One entry per tank build. The site figure is a weighted mean over these. */
  classes?: ClassConsistency[];
}

/**
 * Error bar for one tank build.
 *
 * Kept per class rather than per site because `spread` is only a measurement error when
 * the tanks compared are identical: pool a 45.5 m tank with an 18.4 m one and the spread
 * mostly reflects that they are different tanks. At Corinth that reads 18% pooled against
 * 8% within the largest class.
 */
export interface ClassConsistency {
  radius_m: number;
  /** Tanks in this class. */
  tanks: number;
  /** Of those, how many yielded a shell height. */
  n: number;
  median_m?: number | null;
  mean_m?: number | null;
  sd_m?: number | null;
  spread: number | null;
  usable: boolean;
}

export interface Inventory {
  volume_m3: number;
  volume_bbl: number;
  tanks: number;
  uncertainty_pct: number | null;
  trustworthy: boolean;
}

export interface TankCandidate {
  polygon_id: number;
  name: string;
  lat: number;
  lng: number;
  radius_m: number;
  gaugeable: boolean;
  roof_type: RoofType;
  last_reading: TankReading | null;
}

export interface GaugeStatus {
  enabled: boolean;
  imagery: { source: string; max_zoom: number; attribution: string };
  limits: { min_radius_m: number; max_batch: number };
  roof_types: RoofType[];
}

export interface BatchResult {
  tanks: TankReading[];
  gauged: number;
  refused: number;
  dropped_over_limit: number;
  scene: SceneInfo | null;
  /**
   * The feature's audit trail. `azimuth_measured_deg` comes off the imagery's own shadows;
   * `azimuth_computed_deg` from the scene date under the standard ~10:30 sun-synchronous
   * overpass. Their agreement is what makes the volume figures defensible — fill needs no
   * sun angle at all, volume needs this assumption, so when `agrees` is false the UI stops
   * presenting volumes as measurements.
   */
  sun: {
    azimuth_measured_deg: number | null;
    azimuth_computed_deg: number | null;
    azimuth_spread_deg: number | null;
    azimuth_agreement_deg: number | null;
    tanks_in_azimuth: number | null;
    elevation_deg: number | null;
    agrees: boolean | null;
  };
  consistency: Consistency;
  inventory: Inventory | null;
  note?: string;
}

/**
 * Whether a polygon is a storage tank this feature will look at.
 *
 * Mirrors the backend's `is_tank`, and deliberately narrow for the same reason: a polygon
 * that merely happens to be round is not a tank, and `metadata.category` is what
 * `scripts/import_refinery_layout.py` actually writes. Keep the two in step — the button
 * appearing for something the API then rejects is a worse experience than no button.
 */
export function isTankPolygon(
  polygon: { metadata?: unknown } | null | undefined,
): boolean {
  if (!polygon) return false;
  let meta = polygon.metadata;
  if (typeof meta === 'string') {
    try { meta = JSON.parse(meta); } catch { return false; }
  }
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return false;
  return (meta as { category?: unknown }).category === 'tank';
}

/** Colour for a fill fraction: red when low, amber mid, green when full. */
export function fillColor(fill: number | null | undefined): string {
  if (fill === null || fill === undefined) return '#6b7280';
  if (fill < 0.25) return '#ef4444';
  if (fill < 0.5) return '#f97316';
  if (fill < 0.75) return '#eab308';
  return '#22c55e';
}

/**
 * What an operator can actually do about a refusal.
 *
 * The backend's `reason` says what went wrong and is shown verbatim; this says what to do
 * next, which is a different question and the one an operator standing in front of the
 * screen is asking. Matched on substrings of the reason rather than on a code because the
 * reasons are written for humans first — a miss degrades to no hint, never to a wrong one.
 */
export function refusalHint(reason: string | undefined | null): string | null {
  if (!reason) return null;
  const r = reason.toLowerCase();
  if (r.includes('fixed roof')) {
    return 'Οι δεξαμενές σταθερής οροφής δεν μετρούνται από σκιά — δεν υπάρχει πλωτή οροφή που να κατεβαίνει.';
  }
  if (r.includes('no crescent is visible')) {
    return 'Δηλώστε τον τύπο οροφής παραπάνω. Αν είναι εξωτερική πλωτή οροφή, η δεξαμενή θα αναφερθεί ως γεμάτη· αν είναι σταθερή, δεν μετριέται.';
  }
  if (r.includes('too dark to read') || r.includes('contrast')) {
    return 'Σκουριασμένη ή λερωμένη οροφή: δεν ξεχωρίζει από τη σκιά της σε αυτή τη λήψη. Χρειάζεται νεότερη εικόνα, όχι διαφορετική ρύθμιση.';
  }
  if (r.includes('nothing to measure') || r.includes('ground shadow')) {
    return 'Η σκιά εδάφους πέφτει πάνω σε τοίχο λεκάνης ή γειτονική δεξαμενή. Τρέξτε τη μέτρηση όλου του site — μια όμοια δεξαμενή δίνει το ύψος κελύφους.';
  }
  if (r.includes('below the') && r.includes('floor')) {
    return 'Πολύ μικρή δεξαμενή για αυτή την ανάλυση εικόνας: η ημισέληνος θα ήταν λίγα pixel.';
  }
  if (r.includes('not round')) {
    return 'Το σχήμα δεν είναι κύκλος — μάλλον δεν είναι δεξαμενή, ή το περίγραμμα χρειάζεται διόρθωση.';
  }
  if (r.includes('outside anything physical') || r.includes('contaminated')) {
    return 'Η μέτρηση μολύνθηκε από γειτονική σκιά. Τρέξτε τη μέτρηση όλου του site ώστε ο άξονας του ήλιου να βγει από τις μεγάλες δεξαμενές.';
  }
  return null;
}

/** Percentage with its error bar, or a lower bound when the crescent hit the pixel floor. */
export function fillLabel(reading: TankReading): string {
  const pct = ((reading.fill_fraction ?? 0) * 100).toFixed(0);
  if (reading.at_resolution_limit) return `≥${pct}%`;
  if (reading.fill_sigma) return `${pct}% ± ${(reading.fill_sigma * 100).toFixed(1)}%`;
  return `${pct}%`;
}

/**
 * Auth headers, read from localStorage the same way App.tsx's getAuthHeaders does.
 *
 * Read here rather than threaded through every call so this client cannot drift out of
 * step with the rest of the app's auth.
 */
function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error((detail as { detail?: string }).detail || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function getStatus(): Promise<GaugeStatus> {
  return json<GaugeStatus>(await fetch(`${API}/api/tank-gauge/status`, {
    headers: authHeaders(),
  }));
}

export async function getCandidates(
  scope = 'group',
): Promise<{ tanks: TankCandidate[]; gaugeable: number; total: number }> {
  return json(await fetch(`${API}/api/tank-gauge/candidates?scope=${scope}`, {
    headers: authHeaders(),
  }));
}

export async function gaugeTank(
  polygonId: number, scope = 'group',
): Promise<TankReading & { scene: SceneInfo | null }> {
  return json(await fetch(
    `${API}/api/tank-gauge/polygons/${polygonId}/gauge?scope=${scope}`,
    { method: 'POST', headers: authHeaders() },
  ));
}

export async function gaugeAll(
  scope = 'group',
): Promise<BatchResult> {
  return json(await fetch(`${API}/api/tank-gauge/gauge-batch?scope=${scope}`, {
    method: 'POST', headers: authHeaders(),
  }));
}

export async function setRoofType(
  polygonId: number,
  roofType: RoofType, scope = 'group',
): Promise<{ polygon_id: number; roof_type: RoofType }> {
  return json(await fetch(
    `${API}/api/tank-gauge/polygons/${polygonId}/roof-type?scope=${scope}`,
    {
      method: 'PUT',
      headers: {
        ...authHeaders(), 'Content-Type': 'application/json',
      },
      body: JSON.stringify({ roof_type: roofType }),
    },
  ));
}
